/**
 * Colour mode: dark by DEFAULT, switchable to light, persisted across reloads.
 *
 * Amendment A8 (docs/api-contract.md, Leo explicit) requires Cloudscape with dark mode as the
 * default and a working light-mode switcher.
 *
 * ⚠️ HOW CLOUDSCAPE REPRESENTS THE MODE — read from
 * node_modules/@cloudscape-design/global-styles/index.js rather than assumed:
 *   applyMode(mode, target = document.body) toggles ONE class, `awsui-dark-mode`, on
 *   document.BODY. LIGHT MODE IS THE ABSENCE OF THAT CLASS. There is no `awsui-light-mode`.
 * So "switch to light" means REMOVE a class, and any code that adds a light class, or adds the
 * dark class to an element applyMode() does not own, creates state Cloudscape cannot clear. That
 * exact bug shipped in the first draft of this port and was caught by verify-ui.js measuring the
 * rendered background luminance — not by reading the code.
 *
 * <html> is mirrored deliberately: body's background is transparent, so without the mirror the
 * page gutter and the pre-paint frame stay the wrong colour.
 *
 * index.html applies the stored mode SYNCHRONOUSLY before paint (see the comment there) so the
 * page does not flash white. This module is the single source of truth afterwards.
 */

import { applyMode, Mode } from '@cloudscape-design/global-styles';

export const STORAGE_KEY = 'demo-color-mode';

/**
 * The key this one replaced. Read-only, and read ONLY as a fallback.
 *
 * ⚠️ MIGRATED RATHER THAN RESET, and src/boot.js carries the full reasoning. Short version: the
 * pre-paint script reads this same preference to avoid a white flash, so a rename with no fallback
 * would give every returning visitor a wrong-coloured first frame — a real, visible regression, not
 * a tidy reset. Copying the value forward costs one read on the first visit after the rename.
 * Nothing WRITES the legacy key, so it dies out.
 */
const STORAGE_KEY_LEGACY = 'usps-demo-color-mode';

/** Stored preference, or 'dark' when nothing has been stored yet. */
export function initialMode() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (legacy === 'light' || legacy === 'dark') {
      // boot.js normally migrates this before we get here; do it again in case storage was blocked
      // at that moment, or the shell was served without boot.js.
      try {
        localStorage.setItem(STORAGE_KEY, legacy);
        localStorage.removeItem(STORAGE_KEY_LEGACY);
      } catch (_e2) {
        /* the migration will not persist; the value still applies for this session */
      }
      return legacy;
    }
  } catch (_e) {
    /* localStorage can be unavailable (private mode, blocked storage) — default applies */
  }
  return 'dark';
}

export function applyColorMode(mode) {
  const dark = mode !== 'light';
  // Cloudscape's own call — this is what actually restyles every component. Targets document.body.
  applyMode(dark ? Mode.Dark : Mode.Light);
  // Mirror onto <html>. body has a transparent background, so the page gutter and scrollbars
  // follow html; Cloudscape itself relies on `html:has(body.awsui-dark-mode)` for the same reason.
  const root = document.documentElement;
  root.classList.toggle('awsui-dark-mode', dark);
  root.style.backgroundColor = dark ? '#0f1b2a' : '#ffffff';
  root.dataset.bootMode = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (_e) {
    /* preference simply will not persist; the UI still switches */
  }
}

/**
 * Colours for the BARCODE CANVAS.
 *
 * A canvas is pixels, not CSS: it inherits no custom properties and no design tokens, so the
 * mode must be resolved explicitly or a mode switch leaves the bars invisible (white-on-white
 * / black-on-black). That failure looks fine in code review and only shows up on screen, which
 * is why it is handled here rather than in a stylesheet.
 *
 * AND — more important than theming — a barcode is a SCANNABLE artefact. Scanners expect DARK
 * bars on a LIGHT background; inverting for dark mode would produce a pretty symbol that a real
 * scanner rejects. So dark mode does not invert the symbol. It renders it on a slightly warmer
 * "label paper" white with true-black bars, framed so it reads as a printed label sitting on a
 * dark page instead of looking like an unstyled hole.
 *
 * `contrast` is returned so the caller (and verify-ui.js) can assert legibility numerically
 * rather than trusting a screenshot.
 */
export function barcodeColors(mode) {
  const pair =
    mode === 'light'
      ? { background: '#ffffff', foreground: '#000000' }
      : { background: '#f4f4f4', foreground: '#000000' };
  return { ...pair, contrast: contrastRatio(pair.background, pair.foreground) };
}

function luminance(hex) {
  const n = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16) / 255);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio, 1..21. Used to prove the bars are legible in whichever mode is live. */
export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
